import { useMemo } from 'react'
import { useSnapshot } from 'valtio'
import { debounce } from 'lodash-es'
import Cookies from 'cookies-js'
import { intl } from 'lang/intl'

import { type Comment, type CommentStatus } from '../models/Comment'
import { addComment, setCommentOrder, updateComment } from '../api/commentApi'
import {
  getLiveId,
  getPresenterToken,
  getRole,
} from '../../global/blocs/roomInfo'
import { getLiveToken } from '../../global/blocs/useConnectInfo'
import { showNotice } from '../../Layout/Notices/Notices'

import { chatMessagesState, reorderQuestions, useQuestionIds } from './useChat'

type QuestionGroups = Record<CommentStatus, Comment[]>

const useQuestions = () => {
  const ids = useQuestionIds()
  const { messages } = useSnapshot(chatMessagesState)

  return useMemo(() => {
    const groups: QuestionGroups = { '': [], answered: [], dismissed: [] }
    ids?.forEach((id) => {
      if (id === undefined) return
      const question = messages.get(id)
      if (question) groups[question.status].push(question)
    })

    return { groups }
  }, [ids, messages])
}

const setQuestionStatus = async (commentId: number, status: CommentStatus) => {
  const question = chatMessagesState.messages.get(commentId)
  const previousStatus = question?.status
  if (question) question.status = status

  try {
    await updateComment({
      object_id: getLiveId(),
      comment_id: commentId,
      comment_status: status,
    })
  } catch {
    if (question && previousStatus !== undefined) {
      question.status = previousStatus
    }
    showNotice({
      message: intl.formatMessage({
        defaultMessage: 'Could not update the question',
        id: '3TqzJv',
      }),
      expirationMillis: 5000,
    })
  }
}

const persistQuestionOrder = debounce((order: number[]) => {
  setCommentOrder({
    object_id: getLiveId(),
    order: order.join(','),
  }).catch(() => {
    showNotice({
      message: intl.formatMessage({
        defaultMessage: 'Could not set the order of comments',
        id: '13pz9N',
      }),
      expirationMillis: 5000,
    })
  })
}, 1000)

/**
 * Takes the unanswered questions in their new order — the only sortable group —
 * and appends everything else in its existing order.
 */
const moveQuestion = (unansweredOrder: number[]) => {
  const ids = chatMessagesState.ids.question
  if (!ids) return

  const moved = new Set(unansweredOrder)
  const rest = ids.filter(
    (id): id is number => id !== undefined && !moved.has(id)
  )
  const newOrder = [...unansweredOrder, ...rest]

  reorderQuestions(newOrder)
  persistQuestionOrder(newOrder)
}

const askQuestion = async (content: string, name?: string) => {
  const isAudience = getRole() === 'audience'

  await addComment({
    object_id: getLiveId(),
    object_type: 'live',
    object_token: await getLiveToken(),
    comment_type: 'question',
    content,
    uuid: isAudience ? Cookies.get('uuid') : undefined,
    presenter_token: getPresenterToken(),
    name,
  })
}

export {
  type QuestionGroups,
  useQuestions,
  setQuestionStatus,
  moveQuestion,
  askQuestion,
}
